import type { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { dayjs, parsePlannedDate } from '../lib/utils';

type PlannedWorkoutSummary = {
  id: string | number;
  planned_date: string;
  type?: unknown;
};

type WorkoutWeekHeaderProps = {
  selectedDate: Dayjs;
  plannedWorkouts: PlannedWorkoutSummary[];
  onSelectDate: (date: Dayjs) => void;
};

const workoutDotClasses = (isSelected: boolean) =>
  isSelected ? 'bg-white' : 'bg-brand-cobalt';

const getWeekMonday = (date: Dayjs) =>
  date.startOf('day').subtract((date.day() + 6) % 7, 'day');

const getScrollableDays = (
  plannedWorkouts: PlannedWorkoutSummary[],
  selectedDate: Dayjs,
) => {
  const workoutDates = plannedWorkouts
    .map((workout) => parsePlannedDate(workout.planned_date))
    .filter((date) => date.isValid());
  const baseDates =
    workoutDates.length > 0
      ? [...workoutDates, dayjs()]
      : [dayjs().subtract(7, 'day'), dayjs().add(21, 'day')];
  const dates = baseDates.sort((a, b) => a.valueOf() - b.valueOf());

  const baseStartDate = getWeekMonday(dates[0]);
  const baseEndDate = getWeekMonday(dates[dates.length - 1]).add(6, 'day');
  const selectedWeekStart = getWeekMonday(selectedDate);
  const startDate = selectedWeekStart.isBefore(baseStartDate, 'day')
    ? selectedWeekStart
    : baseStartDate;
  const endDate = selectedWeekStart.isAfter(baseEndDate, 'day')
    ? selectedWeekStart.add(6, 'day')
    : baseEndDate;
  const dayCount = endDate.diff(startDate, 'day') + 1;

  return Array.from({ length: dayCount }, (_, index) =>
    startDate.add(index, 'day'),
  );
};

const getWeeks = (days: Dayjs[]) => {
  const weeks: Dayjs[][] = [];

  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }

  return weeks;
};

const getMonthLabel = (date: Dayjs) => date.format('MMMM YYYY');

const getWorkoutsForDate = (
  plannedWorkouts: PlannedWorkoutSummary[],
  date: Dayjs,
) =>
  plannedWorkouts.filter((workout) => {
    const workoutDate = parsePlannedDate(workout.planned_date).format(
      'YYYY-MM-DD',
    );
    return workoutDate === date.format('YYYY-MM-DD');
  });

export const WorkoutWeekHeader = ({
  selectedDate,
  plannedWorkouts,
  onSelectDate,
}: WorkoutWeekHeaderProps) => {
  const scrollDays = getScrollableDays(plannedWorkouts, selectedDate);
  const weeks = getWeeks(scrollDays);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [visibleMonthLabel, setVisibleMonthLabel] = useState(() =>
    getMonthLabel(selectedDate),
  );

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const selectedWeekMonday = getWeekMonday(selectedDate);
    const selectedWeekPanel = scrollContainer.querySelector<HTMLElement>(
      `[data-week-start="${selectedWeekMonday.format('YYYY-MM-DD')}"]`,
    );

    if (!selectedWeekPanel) return;

    scrollContainer.scrollLeft = selectedWeekPanel.offsetLeft;
    setVisibleMonthLabel(getMonthLabel(selectedWeekMonday));
  }, [scrollDays.length, selectedDate]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const updateVisibleMonth = () => {
      const weeks = Array.from(
        scrollContainer.querySelectorAll<HTMLElement>('[data-week-start]'),
      );
      const nearestWeek = weeks.reduce<HTMLElement | null>((nearest, week) => {
        if (!nearest) return week;

        const nearestDistance = Math.abs(
          nearest.offsetLeft - scrollContainer.scrollLeft,
        );
        const weekDistance = Math.abs(
          week.offsetLeft - scrollContainer.scrollLeft,
        );
        return weekDistance < nearestDistance ? week : nearest;
      }, null);
      const weekStart = nearestWeek?.dataset.weekStart;

      if (!weekStart) return;

      setVisibleMonthLabel(getMonthLabel(parsePlannedDate(weekStart)));
    };

    updateVisibleMonth();
    scrollContainer.addEventListener('scroll', updateVisibleMonth, {
      passive: true,
    });

    return () => {
      scrollContainer.removeEventListener('scroll', updateVisibleMonth);
    };
  }, [scrollDays.length]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('workoutMonthChange', { detail: visibleMonthLabel }),
    );
  }, [visibleMonthLabel]);

  return (
    <div
      aria-label="Workout date picker"
      className="depth-panel overflow-hidden rounded-2xl"
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-white/10">
        <div>
          <p className="text-sm font-bold text-brand-midnight dark:text-white">
            {visibleMonthLabel}
          </p>
          <p className="mt-0.5 text-[11px] font-medium text-gray-500">
            Select a day to see your session
          </p>
        </div>
        <span className="rounded-full bg-brand-voltSoft px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-midnight">
          Week view
        </span>
      </div>
      <div
        ref={scrollContainerRef}
        data-disable-pull-to-refresh
        className="snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex">
          {weeks.map((week) => (
            <div
              key={week[0].format('YYYY-MM-DD')}
              data-week-start={week[0].format('YYYY-MM-DD')}
              className="grid min-w-full snap-start snap-always grid-cols-7 divide-x divide-gray-100 dark:divide-white/15"
            >
              {week.map((date) => {
                const dateWorkouts = getWorkoutsForDate(plannedWorkouts, date);
                const isSelected = date.isSame(selectedDate, 'day');
                const isCurrentDay = date.isSame(dayjs(), 'day');

                return (
                  <button
                    key={date.format('YYYY-MM-DD')}
                    data-date={date.format('YYYY-MM-DD')}
                    type="button"
                    onClick={() => onSelectDate(date)}
                    className={`group relative flex min-h-[3.25rem] flex-col items-center px-1 pb-1 pt-2.5 transition-colors ${
                      isSelected
                        ? 'bg-gradient-to-b from-[#1b2b49] to-brand-midnight text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]'
                        : 'text-brand-midnight hover:bg-brand-voltSoft dark:text-white dark:hover:bg-white dark:hover:text-brand-midnight'
                    }`}
                    aria-label={`View workouts for ${date.format('dddd, MMMM D')}`}
                    aria-pressed={isSelected}
                  >
                    {isCurrentDay ? (
                      <span
                        className="absolute top-1 h-1 w-8 rounded-full bg-brand-volt"
                        aria-hidden="true"
                      />
                    ) : null}
                    <span className="flex h-6 w-6 items-center justify-center text-lg font-black leading-none tracking-[-0.05em]">
                      {date.format('D')}
                    </span>
                    <span
                      className={`mt-0.5 text-[0.65rem] font-bold uppercase leading-none ${
                        isSelected
                          ? 'text-white/75'
                          : 'text-brand-midnight/65 dark:text-white/65 dark:group-hover:text-brand-midnight/65'
                      }`}
                    >
                      {date.format('ddd')}
                    </span>
                    <span className="mt-1 flex h-2 items-center justify-center gap-0.5">
                      {dateWorkouts.length > 0
                        ? dateWorkouts
                            .slice(0, 3)
                            .map((workout) => (
                              <span
                                key={workout.id}
                                className={`h-1.5 w-1.5 rounded-full ${workoutDotClasses(isSelected)}`}
                              />
                            ))
                        : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
